// useAutoLogout.js
import { useEffect, useRef } from 'react';

/**
 * useAutoLogout
 * @param {Object} options
 * @param {number} options.timeout - Inaktivitätszeit in ms (default 10min)
 * @param {Function} options.onLogout - Funktion, die bei Logout ausgeführt wird (z.B. Token löschen + redirect)
 */
export default function useAutoLogout({ timeout = 10 * 60 * 1000, onLogout }) {
  const timerRef = useRef(null);

  // Aktion zum Ausführen des Logouts (auch in anderen Tabs signalisieren)
  const performLogout = () => {
    try {
      // Signal für andere Tabs setzen (kann beliebigen Wert haben)
      localStorage.setItem('autoLogout', Date.now().toString());
    } catch (e) {
      /* ignore */
    }
    if (typeof onLogout === 'function') onLogout();
  };

  // Timer starten/neu starten
  const resetTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      performLogout();
    }, timeout);
  };

  useEffect(() => {
    // Events, die Aktivität bedeuten
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    // Visibility change: wenn Tab wieder sichtbar wird, resetten
    const onVisibilityChange = () => {
      if (!document.hidden) resetTimer();
    };

    // Listener für Storage-Event: wenn ein anderer Tab logout signaliert, dann ausloggen
    const onStorage = (e) => {
      if (!e) return;
      if (e.key === 'autoLogout') {
        // Andere Tab hat Logout ausgelöst
        if (typeof onLogout === 'function') onLogout();
      }
    };

    // Attach listeners
    events.forEach((ev) => window.addEventListener(ev, resetTimer));
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('storage', onStorage);

    // Start initial timer
    resetTimer();

    return () => {
      // Cleanup
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((ev) => window.removeEventListener(ev, resetTimer));
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('storage', onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeout, onLogout]);
}
